import * as React from 'react';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro';
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import type { TaxCode } from '@repo/api-contract';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SalesTaxSearchInput,
  searchSalesTaxCodes,
  setSearchInput,
  setSearchLoading,
  setSelectedTaxCodes,
} from '../../../../state/salesTaxSearchModalSlice';
import { useDispatch, useSelector } from '../../../../state';
import hexToRGBA from '../../../../util/converters/hexToRGBA';

const INITIAL_PAGE_SIZE = 25;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 4,
});

function LoadingOverlay() {
  return <LinearProgress color="primary" />;
}

function CustomToolbar() {
  const dispatch = useDispatch();
  const { t: tGlobalTables } = useTranslation([], {
    keyPrefix: 'team.Global.Tables',
  });
  const { t } = useTranslation([], {
    keyPrefix: 'team.Modals.SalesTaxSearch',
  });
  const { searchInput, searchBy, searchLoading, resp } = useSelector(
    (store) => store.salesTaxSearchModalSlice,
  );
  const { team } = useSelector((store) => store.userSlice);
  const pageSize = resp?.size || INITIAL_PAGE_SIZE;

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: SalesTaxSearchInput = {
        query: searchQuery,
        searchBy,
        size: pageSize,
        page: 0,
      };
      dispatch(searchSalesTaxCodes(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel id="sales-tax-search-by-label">
          {tGlobalTables('Search By')}
        </InputLabel>
        <Select
          labelId="sales-tax-search-by-label"
          value={searchBy}
          label={tGlobalTables('Search By')}
        >
          <MenuItem value="codeName">{t('Tax Code')}</MenuItem>
        </Select>
      </FormControl>
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setSearchLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={t('Search for Sales Tax...')}
        variant="outlined"
        sx={{ minWidth: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {searchLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
            </React.Fragment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
}

interface TableProps {
  multiSelect: boolean;
}

export default function Table({ multiSelect }: TableProps) {
  const dispatch = useDispatch();
  const { resp, searchLoading, searchInput, searchBy } = useSelector(
    (store) => store.salesTaxSearchModalSlice,
  );

  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    let finalSelectionModel = newSelectionModel;
    if (!multiSelect && newSelectionModel.length > 1) {
      const lastId = newSelectionModel[newSelectionModel.length - 1];
      if (lastId !== undefined) {
        finalSelectionModel = [lastId];
      }
    }

    const rows = resp ? resp.results : [];
    const selected = rows.filter((row) =>
      finalSelectionModel.includes(row.TaxCodeId),
    );
    dispatch(setSelectedTaxCodes(selected));
  };

  const columns: GridColDef<TaxCode>[] = React.useMemo(
    () => [
      {
        field: 'codeName',
        headerName: 'Tax Code',
        flex: 1,
        renderHeader: () => <strong>{'Tax Code'}</strong>,
      },
      {
        field: 'rate',
        headerName: 'Tax Rate',
        flex: 1,
        renderHeader: () => <strong>{'Tax Rate'}</strong>,
        renderCell: (params: GridRenderCellParams<TaxCode, number>) => (
          <span>{`${percentFormatter.format(params.value ?? 0)}%`}</span>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        renderHeader: () => <strong>{'Description'}</strong>,
      },
      {
        field: 'calculatedAmount',
        headerName: 'Calculated Amount',
        flex: 1,
        sortable: false,
        renderHeader: () => <strong>{'Calculated Amount'}</strong>,
        renderCell: () => <span>{currencyFormatter.format(0)}</span>,
      },
    ],
    [],
  );

  return (
    <DataGridPro
      columnHeaderHeight={56}
      sx={{
        border: 'none',
        padding: '1rem',
        '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
          backgroundColor: '#ECECEC',
        },
        '.MuiDataGrid-row.Mui-selected': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          whiteSpace: 'normal',
          lineHeight: 1.2,
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
      rows={
        resp
          ? resp.results.map((taxCode) => ({
              ...taxCode,
              id: taxCode.TaxCodeId,
            }))
          : []
      }
      onRowSelectionModelChange={handleSelectionChange}
      checkboxSelection={multiSelect}
      disableRowSelectionOnClick={multiSelect}
      loading={searchLoading}
      columns={columns}
      pagination
      paginationMode="server"
      paginationModel={{
        page: resp?.page || 0,
        pageSize: resp?.size || INITIAL_PAGE_SIZE,
      }}
      rowCount={resp?.total || 0}
      pageSizeOptions={[INITIAL_PAGE_SIZE, 50, 100]}
      onPaginationModelChange={(newModel) => {
        const payload: SalesTaxSearchInput = {
          query: searchInput,
          searchBy,
          page: newModel.page,
          size: newModel.pageSize,
        };
        dispatch(searchSalesTaxCodes(payload));
      }}
      pinnedColumns={
        multiSelect ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : undefined
      }
      disableColumnMenu
      autoHeight={false}
      slots={{
        loadingOverlay: LoadingOverlay,
        toolbar: CustomToolbar,
      }}
    />
  );
}

export { INITIAL_PAGE_SIZE };
